import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface PricingTier {
  id: 'free' | 'basic' | 'premium' | 'lifetime';
  name: string;
  tagline: string;
  price: string;
  priceMonthly: number;
  yearlyPrice?: string;
  savings?: string;
  features: string[];
  cta: string;
  popular?: boolean;
  badge?: string;
  icon?: string;
}

const pricingTiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Perfect for testing the waters',
    price: '$0',
    priceMonthly: 0,
    icon: '🚀',
    features: [
      '3 AI-generated courses',
      'GPT-4 powered content',
      'AI trending topic suggestions',
      'Professional course outlines',
      'Automated quiz generation',
      'Basic marketing templates',
      'PDF & HTML exports',
      'Mobile-friendly builder',
      'Email support',
    ],
    cta: 'Get Started Free',
  },
  {
    id: 'basic',
    name: 'Basic',
    tagline: 'For serious course creators',
    price: '$29',
    priceMonthly: 29,
    yearlyPrice: '$290',
    savings: 'Save $58/year',
    icon: '⚡',
    badge: 'Most Popular',
    popular: true,
    features: [
      '✨ Everything in Free, PLUS:',
      '∞ Unlimited AI-generated courses',
      '📧 AI email sequence generator',
      '📝 Custom email template editor',
      '🎯 AI sales page content generator',
      '📱 30-day social media calendar',
      '🔌 Zapier integration (5000+ apps)',
      '📊 Mailchimp & ConvertKit sync',
      '📈 Google Analytics & FB Pixel',
      '🎨 Custom branding (4 colors, 2 fonts)',
      '🎓 Certificate generation',
      '👥 2 team collaborators',
      '💬 Priority email support',
    ],
    cta: 'Start 14-Day Free Trial',
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'For agencies & power users',
    price: '$47',
    priceMonthly: 47,
    yearlyPrice: '$470',
    savings: 'Save $94/year',
    icon: '👑',
    badge: 'Best Value',
    features: [
      '💎 Everything in Basic, PLUS:',
      '👥 Unlimited team collaborators',
      '💬 Course comments & feedback',
      '🎨 White-label PDF/HTML exports',
      '🎓 Teachable & Thinkific export',
      '🎬 AI Video generation',
      '🖼️ AI marketing image generator',
      '🤖 AI brand voice customization',
      '📊 Course tracking spreadsheet',
      '🖼️ Canva-ready image exports',
      '⚡ Early access to new features',
      '💼 Premium integrations (ElevenLabs, HeyGen)',
      '👨‍💼 Priority email support',
    ],
    cta: 'Start 14-Day Free Trial',
  },
  {
    id: 'lifetime',
    name: 'Lifetime',
    tagline: 'Own it forever',
    price: '$497',
    priceMonthly: 497,
    icon: '💎',
    badge: 'Limited Offer',
    features: [
      '🔓 Lifetime Premium access',
      '♾️ All future updates included',
      '🚫 Never pay monthly again',
      '✅ Unlimited everything',
      '📖 All premium templates included',
      '🔒 Price locked forever',
      '⏰ Only 500 licenses available',
      '🎁 Founding member status',
      '⚡ First access to all new features',
    ],
    cta: 'Claim Lifetime Access',
  },
];

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const handleUpgrade = async (tier: 'basic' | 'premium' | 'lifetime') => {
    if (!user || !token) {
      toast.showToast('Please log in to upgrade', 'error');
      navigate('/login');
      return;
    }

    if (user.tier === tier) {
      toast.showToast('You are already on this plan', 'info');
      return;
    }

    setLoading(tier);

    try {
      const response = await axios.post(
        `${API_URL}/create-checkout-session`,
        { tier },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.showToast('Failed to create checkout session', 'error');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      toast.showToast(error.response?.data?.error || 'Failed to start upgrade process', 'error');
    } finally {
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!token) {
      toast.showToast('Please log in to manage billing', 'error');
      navigate('/login');
      return;
    }

    setLoading('billing');

    try {
      const response = await axios.post(
        `${API_URL}/billing-portal`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.showToast('Failed to open billing portal', 'error');
      }
    } catch (error: any) {
      console.error('Error opening billing portal:', error);
      toast.showToast(error.response?.data?.error || 'Failed to open billing portal', 'error');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900 flex items-center"
              >
                ← Back
              </button>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  The Course Creator's Vault
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            {user && (user.tier === 'basic' || user.tier === 'premium') && (
              <button
                onClick={handleManageBilling}
                disabled={loading === 'billing'}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50"
              >
                {loading === 'billing' ? 'Loading...' : 'Manage Billing'}
              </button>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="border-t border-gray-200">
            <nav className="flex overflow-x-auto space-x-4 sm:space-x-6 py-2 px-1">
              <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Dashboard</button>
              <button onClick={() => navigate('/templates')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Templates</button>
              <button onClick={() => navigate('/brand-settings')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Brand</button>
              <button onClick={() => navigate('/pricing')} className="text-indigo-600 font-semibold whitespace-nowrap text-sm sm:text-base border-b-2 border-indigo-600 pb-2">Pricing</button>
              <button onClick={() => navigate('/help')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Help</button>
              <button onClick={() => navigate('/contact')} className="text-gray-600 hover:text-gray-900 whitespace-nowrap text-sm sm:text-base">Contact</button>
            </nav>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
            Choose Your Plan
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
            Transform your expertise into profitable online courses with AI. Start free, upgrade anytime.
          </p>

          {/* Social Proof */}
          <div className="flex flex-wrap justify-center items-center gap-8 mb-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">2,847+</div>
              <div className="text-sm text-gray-600">Course Creators</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">89,000+</div>
              <div className="text-sm text-gray-600">Courses Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">$12.4M+</div>
              <div className="text-sm text-gray-600">Revenue Generated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">⭐ 4.9/5</div>
              <div className="text-sm text-gray-600">User Rating</div>
            </div>
          </div>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-lg p-1 shadow-md">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md transition ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                  : 'text-gray-600'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md transition relative ${
                billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                  : 'text-gray-600'
              }`}
            >
              Yearly
              <span className="absolute -top-3 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {pricingTiers.map((tier) => {
            const isCurrentTier = user?.tier === tier.id;
            const canUpgrade = user && tier.id !== 'free' && !isCurrentTier;
            const displayPrice = billingCycle === 'yearly' && tier.yearlyPrice ? tier.yearlyPrice : tier.price;

            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl ${
                  tier.popular ? 'ring-2 ring-purple-600 lg:scale-105' : ''
                } ${isCurrentTier ? 'ring-2 ring-green-500' : ''}`}
              >
                {/* Badge */}
                {tier.badge && !isCurrentTier && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className={`px-4 py-1 rounded-full text-sm font-semibold text-white ${
                      tier.popular ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'bg-gradient-to-r from-orange-500 to-red-500'
                    }`}>
                      {tier.badge}
                    </span>
                  </div>
                )}

                {isCurrentTier && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-green-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Current Plan
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">{tier.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{tier.name}</h3>
                  <p className="text-sm text-gray-600">{tier.tagline}</p>
                </div>

                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      {displayPrice}
                    </span>
                    {tier.priceMonthly > 0 && tier.id !== 'lifetime' && (
                      <span className="text-gray-500 ml-2">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    )}
                    {tier.id === 'lifetime' && (
                      <span className="text-gray-500 ml-2 text-sm">once</span>
                    )}
                  </div>
                  {billingCycle === 'yearly' && tier.savings && (
                    <p className="text-green-600 text-sm font-semibold">{tier.savings}</p>
                  )}
                  {tier.id === 'lifetime' && (
                    <p className="text-sm text-gray-600">vs. $564/year subscription</p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <svg
                        className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => {
                    if (canUpgrade) {
                      handleUpgrade(tier.id as 'basic' | 'premium' | 'lifetime');
                    } else if (tier.id === 'free' && !user) {
                      navigate('/signup');
                    }
                  }}
                  disabled={isCurrentTier || loading === tier.id}
                  className={`w-full py-4 rounded-lg font-semibold transition-all duration-300 ${
                    tier.popular && !isCurrentTier
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-xl hover:scale-105'
                      : isCurrentTier
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : tier.id === 'lifetime'
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:shadow-xl hover:scale-105'
                      : 'bg-gray-900 text-white hover:bg-gray-800 hover:shadow-lg'
                  }`}
                >
                  {loading === tier.id
                    ? 'Processing...'
                    : isCurrentTier
                    ? 'Current Plan'
                    : canUpgrade
                    ? tier.cta
                    : tier.id === 'free' && !user
                    ? 'Get Started Free'
                    : tier.cta}
                </button>

                {tier.id === 'lifetime' && (
                  <p className="mt-3 text-center text-xs text-gray-600">
                    489/500 claimed • Almost sold out!
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4">Feature</th>
                  <th className="text-center py-4 px-4">Free</th>
                  <th className="text-center py-4 px-4 bg-purple-50">Basic</th>
                  <th className="text-center py-4 px-4">Premium</th>
                  <th className="text-center py-4 px-4">Lifetime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-4 px-4 font-medium">AI Courses</td>
                  <td className="text-center py-4 px-4">3</td>
                  <td className="text-center py-4 px-4 bg-purple-50">∞</td>
                  <td className="text-center py-4 px-4">∞</td>
                  <td className="text-center py-4 px-4">∞</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium">AI Marketing Tools</td>
                  <td className="text-center py-4 px-4">Templates</td>
                  <td className="text-center py-4 px-4 bg-purple-50">Full AI</td>
                  <td className="text-center py-4 px-4">Full AI + Images</td>
                  <td className="text-center py-4 px-4">Full AI + Images</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium">Team Collaborators</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4 bg-purple-50">2</td>
                  <td className="text-center py-4 px-4">∞</td>
                  <td className="text-center py-4 px-4">∞</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium">Integrations</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4 bg-purple-50">5</td>
                  <td className="text-center py-4 px-4">All (9+)</td>
                  <td className="text-center py-4 px-4">All (9+)</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium">AI Video Generation</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4 bg-purple-50">❌</td>
                  <td className="text-center py-4 px-4">✅</td>
                  <td className="text-center py-4 px-4">✅</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium">White-Label Exports</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4 bg-purple-50">❌</td>
                  <td className="text-center py-4 px-4">✅</td>
                  <td className="text-center py-4 px-4">✅</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 font-medium">Teachable/Thinkific</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4 bg-purple-50">❌</td>
                  <td className="text-center py-4 px-4">✅</td>
                  <td className="text-center py-4 px-4">✅</td>
                </tr>
                <tr className="bg-gray-50 font-bold">
                  <td className="py-4 px-4">Price/Year</td>
                  <td className="text-center py-4 px-4">$0</td>
                  <td className="text-center py-4 px-4 bg-purple-100">$348</td>
                  <td className="text-center py-4 px-4">$564</td>
                  <td className="text-center py-4 px-4 text-green-600">$497 once</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6 max-w-3xl mx-auto">
            <div>
              <h3 className="font-semibold text-lg mb-2">Can I switch plans anytime?</h3>
              <p className="text-gray-600">Yes! Upgrade or downgrade anytime. Changes take effect immediately.</p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">What happens to my courses if I downgrade?</h3>
              <p className="text-gray-600">Your courses are safe! You keep all created content. On Free plan, you can view all courses but only create 3.</p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Can I try it before paying?</h3>
              <p className="text-gray-600">Yes! Our Free tier lets you create 3 full AI courses with all core features. Upgrade anytime when you need more.</p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">What's your refund policy?</h3>
              <p className="text-gray-600">Since this is a digital product, we don't offer refunds. Use the Free tier to explore the platform before upgrading!</p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-600">Yes, cancel anytime from your billing portal. No cancellation fees.</p>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="text-center mb-16">
          <p className="text-gray-600 mb-6 text-lg">Trusted by course creators worldwide</p>
          <div className="flex flex-wrap justify-center items-center gap-8">
            <div className="text-gray-500">🔒 Secure Payment</div>
            <div className="text-gray-500">💳 Stripe Powered</div>
            <div className="text-gray-500">🚫 Cancel Anytime</div>
            <div className="text-gray-500">✅ 30-Day Guarantee</div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-gray-600 mb-4 text-lg">
            All plans include access to our GPT-4 powered AI course generation
          </p>
          <p className="text-gray-600">
            Need a custom enterprise plan?{' '}
            <a href="mailto:sloane@templimizeai.com" className="text-purple-600 hover:underline font-semibold">
              Contact us
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Pricing;
